import { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader } from './ui/Card';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Toggle } from './ui/Toggle';
import { useConfirm } from '../context/ConfirmContext';

const fieldLabelClass = 'text-sm font-semibold text-text';
const fieldInputClass =
  'text-lg font-semibold tabular-nums bg-card border-primary/20 focus:border-primary';

function itemFormHasData(form, itemType) {
  return form[`${itemType}_boys`].trim() !== '' || form[`${itemType}_girls`].trim() !== '';
}

function itemRecordExists(records, itemType) {
  return records.some((r) => r.item_type === itemType);
}

function itemCanSave(form, itemType) {
  return form[`${itemType}_boys`].trim() !== '' && form[`${itemType}_girls`].trim() !== '';
}

function NutritionItemBlock({
  itemLabel,
  itemId,
  boys,
  girls,
  maxBoys,
  maxGirls,
  onBoysChange,
  onGirlsChange,
  onSave,
  canSave,
  isLoading,
  disabled,
  saved,
  labels,
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-base font-semibold text-text">{itemLabel}</p>
        {saved && <Badge variant="success">{labels.saved}</Badge>}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <Input
          label={labels.boys}
          id={`${itemId}-boys`}
          name={`${itemId}-boys`}
          labelClassName={fieldLabelClass}
          inputClassName={fieldInputClass}
          type="number"
          min={0}
          max={maxBoys > 0 ? maxBoys : undefined}
          inputMode="numeric"
          autoComplete="off"
          value={boys}
          onChange={(e) => onBoysChange(e.target.value)}
        />
        <Input
          label={labels.girls}
          id={`${itemId}-girls`}
          name={`${itemId}-girls`}
          labelClassName={fieldLabelClass}
          inputClassName={fieldInputClass}
          type="number"
          min={0}
          max={maxGirls > 0 ? maxGirls : undefined}
          inputMode="numeric"
          autoComplete="off"
          value={girls}
          onChange={(e) => onGirlsChange(e.target.value)}
        />
      </div>
      {!canSave && (
        <p className="text-center text-xs text-text/80">{labels.nutritionSaveHint}</p>
      )}
      <Button
        className="w-full"
        size="lg"
        onClick={onSave}
        isLoading={isLoading}
        disabled={disabled || !canSave}
      >
        {labels.save} {itemLabel}
      </Button>
    </div>
  );
}

function NutritionSectionPanel({ title, savedBadges, children }) {
  return (
    <div className="space-y-4 rounded-xl border-2 border-primary/20 bg-primary-light/10 p-4">
      <div className="flex flex-wrap items-center gap-2 border-b border-border/60 pb-3">
        <h3 className="text-base font-semibold text-text">{title}</h3>
        {savedBadges}
      </div>
      {children}
    </div>
  );
}

export function TeacherNutritionSection({
  labels,
  nutritionForm,
  setNutritionForm,
  nutritionRecords,
  selectedDate,
  classId,
  fetchTeacherNutrition,
  updateNutritionItem,
  updateNutritionMilk,
  clearNutritionItems,
  isLoading,
  subscriptionWritesBlocked,
  maxBoys = 0,
  maxGirls = 0,
}) {
  const { confirm } = useConfirm();
  const [eggBananaOn, setEggBananaOn] = useState(false);
  const [milkOn, setMilkOn] = useState(false);

  useEffect(() => {
    if (!classId || !selectedDate) return;
    const dateIso = selectedDate?.slice?.(0, 10) || selectedDate;
    fetchTeacherNutrition(classId, dateIso);
  }, [classId, selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setEggBananaOn(false);
    setMilkOn(false);
  }, [classId, selectedDate]);

  useEffect(() => {
    if (itemRecordExists(nutritionRecords, 'egg') || itemRecordExists(nutritionRecords, 'banana')) {
      setEggBananaOn(true);
    }
    if (itemRecordExists(nutritionRecords, 'milk')) {
      setMilkOn(true);
    }
  }, [nutritionRecords]);

  const clearItemFields = (items) => {
    setNutritionForm((prev) => {
      const next = { ...prev };
      items.forEach((item) => {
        next[`${item}_boys`] = '';
        next[`${item}_girls`] = '';
      });
      return next;
    });
  };

  const handleEggBananaToggle = async (next) => {
    if (next) {
      setEggBananaOn(true);
      return;
    }
    const hasSaved =
      itemRecordExists(nutritionRecords, 'egg') ||
      itemRecordExists(nutritionRecords, 'banana') ||
      itemFormHasData(nutritionForm, 'egg') ||
      itemFormHasData(nutritionForm, 'banana');
    if (!hasSaved) {
      setEggBananaOn(false);
      clearItemFields(['egg', 'banana']);
      return;
    }
    const ok = await confirm({
      title: labels.nutritionClearTitle,
      message: labels.nutritionClearEggBanana,
      confirmLabel: labels.yes,
      cancelLabel: labels.no,
    });
    if (!ok) return;
    const items = [];
    if (itemFormHasData(nutritionForm, 'egg') || itemRecordExists(nutritionRecords, 'egg')) {
      items.push('egg');
    }
    if (itemFormHasData(nutritionForm, 'banana') || itemRecordExists(nutritionRecords, 'banana')) {
      items.push('banana');
    }
    const cleared = await clearNutritionItems(items);
    if (cleared) {
      setEggBananaOn(false);
      clearItemFields(items);
    }
  };

  const handleMilkToggle = async (next) => {
    if (next) {
      setMilkOn(true);
      return;
    }
    const hasSaved =
      itemFormHasData(nutritionForm, 'milk') || itemRecordExists(nutritionRecords, 'milk');
    if (!hasSaved) {
      setMilkOn(false);
      clearItemFields(['milk']);
      return;
    }
    const ok = await confirm({
      title: labels.nutritionClearTitle,
      message: labels.nutritionClearMilk,
      confirmLabel: labels.yes,
      cancelLabel: labels.no,
    });
    if (!ok) return;
    const cleared = await clearNutritionItems(['milk']);
    if (cleared) {
      setMilkOn(false);
      clearItemFields(['milk']);
    }
  };

  const eggSaved = itemRecordExists(nutritionRecords, 'egg');
  const bananaSaved = itemRecordExists(nutritionRecords, 'banana');
  const milkSaved = itemRecordExists(nutritionRecords, 'milk');

  const canSaveMilk = itemCanSave(nutritionForm, 'milk');

  return (
    <Card>
      <CardHeader title={labels.nutritionTitle} subtitle={labels.nutritionSubtitle} />
      <CardBody className="space-y-4">
        <Toggle
          label={labels.nutritionToggleEggBanana}
          checked={eggBananaOn}
          onChange={handleEggBananaToggle}
          disabled={subscriptionWritesBlocked}
        />

        {eggBananaOn && (
          <NutritionSectionPanel
            title={labels.nutritionEggBananaSection}
            savedBadges={
              <>
                {eggSaved && <Badge variant="success">{labels.nutritionEgg} ✓</Badge>}
                {bananaSaved && <Badge variant="success">{labels.nutritionBanana} ✓</Badge>}
              </>
            }
          >
            <div className="space-y-3">
              <NutritionItemBlock
                itemLabel={labels.nutritionEgg}
                itemId="nutrition-egg"
                boys={nutritionForm.egg_boys}
                girls={nutritionForm.egg_girls}
                maxBoys={maxBoys}
                maxGirls={maxGirls}
                labels={labels}
                saved={eggSaved}
                canSave={itemCanSave(nutritionForm, 'egg')}
                onSave={() => updateNutritionItem('egg')}
                isLoading={isLoading.updateNutrition}
                disabled={subscriptionWritesBlocked}
                onBoysChange={(v) => setNutritionForm({ ...nutritionForm, egg_boys: v })}
                onGirlsChange={(v) => setNutritionForm({ ...nutritionForm, egg_girls: v })}
              />
              <NutritionItemBlock
                itemLabel={labels.nutritionBanana}
                itemId="nutrition-banana"
                boys={nutritionForm.banana_boys}
                girls={nutritionForm.banana_girls}
                maxBoys={maxBoys}
                maxGirls={maxGirls}
                labels={labels}
                saved={bananaSaved}
                canSave={itemCanSave(nutritionForm, 'banana')}
                onSave={() => updateNutritionItem('banana')}
                isLoading={isLoading.updateNutrition}
                disabled={subscriptionWritesBlocked}
                onBoysChange={(v) => setNutritionForm({ ...nutritionForm, banana_boys: v })}
                onGirlsChange={(v) => setNutritionForm({ ...nutritionForm, banana_girls: v })}
              />
            </div>
          </NutritionSectionPanel>
        )}

        <Toggle
          label={labels.nutritionToggleMilk}
          checked={milkOn}
          onChange={handleMilkToggle}
          disabled={subscriptionWritesBlocked}
        />

        {milkOn && (
          <div className="space-y-4 rounded-xl border-2 border-primary/20 bg-primary-light/10 p-4">
            <div className="flex flex-wrap items-center gap-2 border-b border-border/60 pb-3">
              <h3 className="text-base font-semibold text-text">{labels.nutritionMilkSection}</h3>
              {milkSaved && <Badge variant="success">{labels.saved}</Badge>}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <Input
                label={labels.boys}
                id="nutrition-milk-boys"
                name="nutrition-milk-boys"
                labelClassName={fieldLabelClass}
                inputClassName={fieldInputClass}
                type="number"
                min={0}
                max={maxBoys > 0 ? maxBoys : undefined}
                inputMode="numeric"
                autoComplete="off"
                value={nutritionForm.milk_boys}
                onChange={(e) => setNutritionForm({ ...nutritionForm, milk_boys: e.target.value })}
              />
              <Input
                label={labels.girls}
                id="nutrition-milk-girls"
                name="nutrition-milk-girls"
                labelClassName={fieldLabelClass}
                inputClassName={fieldInputClass}
                type="number"
                min={0}
                max={maxGirls > 0 ? maxGirls : undefined}
                inputMode="numeric"
                autoComplete="off"
                value={nutritionForm.milk_girls}
                onChange={(e) => setNutritionForm({ ...nutritionForm, milk_girls: e.target.value })}
              />
            </div>
            {!canSaveMilk && (
              <p className="text-center text-xs text-text/80">{labels.nutritionSaveHint}</p>
            )}
            <Button
              className="w-full"
              size="lg"
              onClick={updateNutritionMilk}
              isLoading={isLoading.updateNutrition}
              disabled={subscriptionWritesBlocked || !canSaveMilk}
            >
              {labels.save} {labels.nutritionMilk}
            </Button>
          </div>
        )}

        {!eggBananaOn && !milkOn && (
          <p className="rounded-lg border border-dashed border-border bg-surface/40 px-4 py-3 text-sm text-text/90">
            {labels.nutritionToggleHint}
          </p>
        )}
      </CardBody>
    </Card>
  );
}
